<template>
  <fullscreen-dialog
    v-model="model"
    :title="$t('warehouse.return_goods')"
    icon="mdi-backup-restore"
    color="pink"
    :save-text="$t('warehouse.return')"
    :disabled="!valid || !editedItem.groceries.length"
    :loading="loading"
    @save="save"
  >
    <template v-slot:app-bar-actions>
      <print-button @click="print"></print-button>
    </template>

    <v-form ref="form" v-model="valid">
      <v-row>
        <v-col cols="12" lg="3" xl="2">
          <choose-warehouse-field
            return-object
            v-model="editedItem.warehouse"
            hide-details
            color="white"
          />
        </v-col>
        <v-col cols="12" lg="2" xl="2">
          <text-field
            :label="$t('warehouse.document_number')"
            v-model="editedItem.number"
            hide-details
            color="white"
          />
        </v-col>
        <v-col cols="12" lg="2" xl="2">
          <date-picker v-model="editedItem.document_date" single-date color="white" />
        </v-col>
        <v-col cols="12" lg="2" xl="2">
          <autocomplete
            v-model="editedItem.dispatched_by"
            :items="users"
            item-text="fullName"
            item-value="id"
            :label="$t('warehouse.returned_product')"
            :rules="[rules.required]"
            color="white"
            required
          />
        </v-col>
        <v-col cols="12" lg="3" xl="2">
          <text-area
            :label="$t('note')"
            v-model="editedItem.notes"
            rows="1"
            color="white"
          />
        </v-col>
      </v-row>
    </v-form>

    <warehouse-purchase-order-table
      v-if="selectedWarehouse"
      :groceries="editedItem.groceries"
      :warehouse="selectedWarehouse"
      :headers="headers"
      return
      @removeGrocery="removeGrocery"
    >
    </warehouse-purchase-order-table>
    <div
      class="text-center grey--text text--darken-1 caption mt-2"
      v-if="selectedWarehouse"
    >
      <v-icon small color="grey darken-1">mdi-keyboard-return</v-icon>
      {{ $t("warehouse.quick_add_hint") }}
    </div>
  </fullscreen-dialog>
</template>

<script>
import hasModel from "@/mixins/hasModel";
import hasValidation from "@/mixins/hasValidation";
import hasPrinting from "@/mixins/hasPrinting";
import hasSelectedWarehouse from "@/mixins/warehouse/hasSelectedWarehouse";
import ChooseWarehouseField from "@/components/warehouse/field/ChooseWarehouseField.vue";
import WarehousePurchaseOrderTable from "@/components/warehouse/requirement/purchase/WarehousePurchaseOrderTable.vue";
import hasSnackbar from "@/mixins/hasSnackbar";
import { returnDocumentGrocery } from "@/models/warehouse";
import warehouseService from "@/services/warehouse";
import FullscreenDialog from "@/components/dialogs/FullscreenDialog.vue";

export default {
  name: "WarehouseDialogOrder",
  mixins: [hasModel, hasValidation, hasPrinting, hasSnackbar, hasSelectedWarehouse],
  components: {
    ChooseWarehouseField,
    WarehousePurchaseOrderTable,
    FullscreenDialog,
  },
  props: {
    editedItem: {
      type: Object,
      default: returnDocumentGrocery,
    },
  },
  data() {
    return {
      search: "",
      valid: false,
      loadingUsers: false,
      loading: false,
    };
  },
  methods: {
    removeGrocery(grocery) {
      this.editedItem.groceries = this.editedItem.groceries.filter(
        (item) => grocery.id !== item.id
      );
    },
    print() {
      const headers = this.headers.filter((header) => header.value !== "actions");
      const items = this.editedItem.groceries.map((item, index) => ({
        ...item,
        num: index + 1,
      }));

      const note = this.editedItem.notes
        ? `
            <div style="font-size: 12px;margin-top: 20px;">
            ${this.$t("note")}: ${this.editedItem.notes}:
            </div>
          `
        : "";
      const dispatchedBy = this.users.find(
        (user) => user.id === this.editedItem.dispatched_by
      );
      this.printDocument({
        tables: [{ headers, items }],
        title: this.$t("warehouse.return_number") + " " + this.editedItem.number,
        beforeTitleHtml: `

            <h2 style="margin-bottom: 0px">${this.$t("stock")}: ${this.toLatin(
          this.editedItem.warehouse?.name || ""
        )}</h2>
              <div style="font-size: 16px;margin-top: 20px;">
                ${this.$t("date")}: ${this.localeDate(this.editedItem.document_date)}
              </div>
            `,
        afterTableHtml: note,
        signature: {
          left: {
            title: this.$t("warehouse.returned_product"),
            name: [dispatchedBy?.name, dispatchedBy?.lastname]
              .map((name) => this.toLatin(name))
              .join(" "),
          },
        },
        hideDatetime: true,
      });
    },
    async save() {
      const groceries = this.editedItem.groceries.map((item) => ({
        grocery_id: item.id,
        warehouse_id: this.editedItem.warehouse.id,
        quantity: item.quantity,
        measure: item.measure,
        transaction_type: "return",
      }));
      const params = {
        ...this.editedItem,
        warehouse_id: this.editedItem.warehouse.id,
        groceries,
      };

      const request = warehouseService.addDocumentTransaction(params);
      try {
        this.loading = true;
        const { data } = await this.handleRequest(request, {
          successMessage: this.$t("warehouse.return_created_successfully"),
          errorMessagePrefix: this.$t("error"),
        });
        this.$emit("groceriesWrittenOff", data); // Emit success event
        this.model = false; // Close dialog on success
      } catch (error) {
        console.error("Save operation failed:", error);
      } finally {
        this.loading = false; // Reset loading state
      }
    },
    async fetchUsers(refetch = false) {
      try {
        this.loadingUsers = true;
        await this.$store.dispatch("restaurantStore/dispatchUsers", {
          refetch,
        });
      } catch (error) {
        console.error("Error fetching users:", error);
      } finally {
        this.loadingUsers = false;
      }
    },
  },
  computed: {
    users() {
      return this.$store.state.restaurantStore.users.map((user) => ({
        ...user,
        fullName: [user.name, user.lastname].map((name) => this.toLatin(name)).join(" "),
      }));
    },
    headers() {
      return [
        { text: this.$t("r_b"), value: "num", width: "100px" },
        {
          text: this.$t("code"),
          align: "start",
          value: "code",
          width: "150px",
        },
        { text: this.$t("title"), value: "name" },
        { text: this.$t("quantity"), value: "quantity" },
        { text: this.$t("warehouse.warehouse_status"), value: "warehouse_stock" },
        { text: "", value: "actions", width: "100px" },
      ];
    },
  },
  watch: {
    model: {
      handler() {
        if (!this.users.length) {
          this.fetchUsers();
        }
      },
      immediate: true,
    },
  },
};
</script>
